import BomHelper from 'store/lib/BomHelper';
import { defineStore } from 'pinia';
import Part from './Part';

const REFRESH_DELAY = 3000;

export default defineStore('bom', {
  state: () => ({
    loading: true,
    error: null,
    pricesOutdated: true,
    helper: null,
    rawParts: [],
    mpnPropertyKey: null,
    availableMpnPropertyKeys: [],
    jobStatus: [],
    availablePrices: [],
    quantityPrice: null,
    previewPrices: [],
    runningOrder: null,
    checkoutPath: null,
    focusedStatus: null,
  }),
  getters: {
    areJobsRunning() {
      return this.jobStatus.some((j) => j.isProcessing);
    },
    areForegroundJobsRunning() {
      const optimization = this.jobStatus.find((j) => j.name === 'optimizing_process');
      return this.areJobsRunning && !(optimization.isProcessing && this.quantityPrice?.calculated);
    },
    parts() {
      const priceMap = new Map();
      if (this.quantityPrice !== null) {
        this.quantityPrice.parts.forEach((p) => priceMap.set(p.id, p));
      }
      return this.rawParts.map((part) => {
        const priceDetails = priceMap.get(part.id) || null;
        return new Part({
          ...part, availability: priceDetails?.availability, priceCents: priceDetails?.priceCents,
        });
      });
    },
  },
  actions: {
    initialize(endpoints) {
      this.helper = new BomHelper(endpoints);
      this.checkoutPath = endpoints.checkoutPath || null;
    },
    updateStatus(newStatus) {
      this.jobStatus = newStatus.jobStatus;
      this.pricesOutdated = newStatus.pricesOutdated;
      this.availablePrices = newStatus.availablePrices;
      this.previewPrices = newStatus.previewPrices;
      this.runningOrder = newStatus.runningOrder;
    },
    invalidatePrices(changedParts = []) {
      this.pricesOutdated = true;
      const changedPartIds = changedParts.map((p) => p.id);
      this.quantityPrice.parts = this.quantityPrice.parts.filter((p) => !changedPartIds.includes(p.id));
    },
    async fetchBom() {
      try {
        this.loading = true;
        const data = await this.helper.fetch();
        this.rawParts = data.parts;
        this.mpnPropertyKey = data.mpnPropertyKey;
        this.availableMpnPropertyKeys = data.availableMpnPropertyKeys;
        this.quantityPrice = data.price;
        this.updateStatus(data.status);
      } catch {
        this.error = true;
        this.invalidatePrices();
      }
      this.loading = false;
    },
    async fetchStatus() {
      const status = await this.helper.fetchStatus();
      this.updateStatus(status);
    },
    async fetchPrices(quantity) {
      this.quantityPrice = await this.helper.fetchPrices(quantity);
    },
    async refreshPrices() {
      const status = await this.helper.refreshPrices();
      this.invalidatePrices();
      this.updateStatus(status);
      await this.waitForJobCompletion(true);
      await this.fetchPrices(1);
      await this.waitForJobCompletion();
    },
    async waitForJobCompletion(firstPriceSufficient = false) {
      /* eslint-disable no-await-in-loop */
      do {
        await this.fetchStatus();
        if (firstPriceSufficient && this.availablePrices.includes(1)) {
          return;
        }
        await new Promise((resolve) => {
          setTimeout(resolve, REFRESH_DELAY);
        });
      } while (this.areJobsRunning);
      /* eslint-enable no-await-in-loop */
    },
    updateParts(changedParts) {
      const changedPartIds = changedParts.map((p) => p.id);
      this.rawParts = [
        ...this.parts.filter((element) => !changedPartIds.includes(element.id)),
        ...changedParts,
      ];
      // Leave the focus once the user has handled every part in it
      if (this.focusedStatus && !this.parts.some((part) => part.getStatus() === this.focusedStatus)) {
        this.clearFocus();
      }
    },
    async requestPartUpdate(updateCall) {
      this.loading = true;

      try {
        const changedParts = await updateCall();
        this.updateParts(changedParts);
        if (this.areJobsRunning) {
          await this.fetchStatus();
        }
        this.invalidatePrices(changedParts);
      } catch (err) {
        this.error = err;
      }

      this.loading = false;
    },
    async setMpnPropertyKey(mpnPropertyKey) {
      this.loading = true;
      try {
        const status = await this.helper.updateMpnPropertyKey(mpnPropertyKey);
        this.mpnPropertyKey = mpnPropertyKey;
        this.clearFocus();
        this.invalidatePrices(this.rawParts);
        this.updateStatus(status);
      } catch (err) {
        this.error = err;
        this.loading = false;
        return;
      }

      await this.waitForJobCompletion();
      await this.fetchBom();
    },
    focusStatus(status) {
      this.focusedStatus = status;
    },
    clearFocus() {
      this.focusedStatus = null;
    },
    async excludeParts(parts, excluded) {
      await this.requestPartUpdate(() => this.helper.excludeParts(parts, excluded));
    },
    async setSourcing(parts, sourcing) {
      const customerSupplied = sourcing === Part.SOURCING.CUSTOMER;
      await this.requestPartUpdate(() => this.helper.setSourcing(parts, customerSupplied));
    },
    async confirmParts(parts) {
      await this.requestPartUpdate(() => this.helper.confirmParts(parts));
    },
    async unassignParts(parts) {
      await this.requestPartUpdate(() => this.helper.unassignParts(parts));
    },
  },
});
